import { BaseButton } from '../base/BaseButton';
import { BaseBadge } from '../base/BaseBadge';
import { BaseSelect } from '../base/BaseSelect';
import { useMonkeysLocale } from '../../provider/context';

export interface RuntimeSourceControlFile { path: string; status: string; indexStatus?: string; worktreeStatus?: string }
export interface RuntimeSourceControlCommit { id: string; shortId: string; message: string; author?: string; date?: string; refs?: readonly string[]; files: readonly { path: string; status?: string }[] }
export interface RuntimeSourceControlModel {
  scopeId: string;
  revision: string;
  repositories: readonly { id: string; name: string; branch?: string; upstream?: string; ahead?: number; behind?: number; changes: readonly RuntimeSourceControlFile[]; commits: readonly RuntimeSourceControlCommit[] }[];
  selectedRepositoryId?: string;
  expandedCommitIds: readonly string[];
}
type SourceControlAction = (
  { action: 'refresh' } |
  { action: 'select-repository'; repositoryId: string } |
  { action: 'open-file' | 'open-diff'; repositoryId: string; path: string } |
  { action: 'toggle-commit'; repositoryId: string; commitId: string; expanded: boolean }
);
export type RuntimeSourceControlIntent = { scopeId: string; expectedRevision: string } & SourceControlAction;
export interface RuntimeSourceControlViewProps {
  model: RuntimeSourceControlModel;
  labels: { repositories: string; branch: string; upstream: string; ahead: string; behind: string; status: string; history: string; refresh: string; openFile: string; openDiff: string; empty: string; loading: string; remainingFiles: string; unknownDate: string };
  locale?: string;
  timeZone?: string;
  disabled?: boolean;
  loading?: boolean;
  onIntent?: (intent: RuntimeSourceControlIntent) => void;
}

export function RuntimeSourceControlView({ model, labels, locale, timeZone, disabled = false, loading = false, onIntent }: RuntimeSourceControlViewProps) {
  const { locale: providerLocale } = useMonkeysLocale();
  const blocked = disabled || loading;
  const repository = model.repositories.find(item => item.id === model.selectedRepositoryId);
  const emit = (intent: SourceControlAction) => {
    if (!blocked) onIntent?.({ ...intent, scopeId: model.scopeId, expectedRevision: model.revision });
  };
  let dateFormatter: Intl.DateTimeFormat | undefined;
  try { dateFormatter = new Intl.DateTimeFormat(locale ?? providerLocale, { dateStyle: 'medium', timeStyle: 'short', timeZone }); } catch { /* Invalid locale/time zone renders the explicit date placeholder. */ }
  return <section aria-busy={loading} className="flex min-h-0 flex-col gap-3 text-sm">
    <div className="flex items-center gap-2">
      <label>{labels.repositories}<BaseSelect aria-label={labels.repositories} disabled={blocked} value={model.selectedRepositoryId ?? ''} onChange={event => emit({ action: 'select-repository', repositoryId: event.currentTarget.value })}>
        <option value="" disabled>{labels.repositories}</option>{model.repositories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </BaseSelect></label>
      <BaseButton size="sm" disabled={blocked} onClick={() => emit({ action: 'refresh' })}>{labels.refresh}</BaseButton>
    </div>
    {loading && <p role="status">{labels.loading}</p>}
    {!repository ? <p>{labels.empty}</p> : <>
      <dl className="flex flex-wrap gap-3"><div><dt>{labels.branch}</dt><dd>{repository.branch ?? '—'}</dd></div>{repository.upstream && <div><dt>{labels.upstream}</dt><dd>{repository.upstream}</dd></div>}<div><dt>{labels.ahead}</dt><dd>{repository.ahead ?? 0}</dd></div><div><dt>{labels.behind}</dt><dd>{repository.behind ?? 0}</dd></div></dl>
      <h3>{labels.status}</h3>
      {repository.changes.length === 0 && <p>{labels.empty}</p>}
      <ul className="flex flex-col gap-1">{repository.changes.map(file => <li key={file.path} className="flex flex-wrap items-center gap-2">
        <BaseBadge label={file.status} /><span className="min-w-0 break-all">{file.path}</span>
        {file.indexStatus && <code>{file.indexStatus}</code>}{file.worktreeStatus && <code>{file.worktreeStatus}</code>}
        <BaseButton size="sm" disabled={blocked} onClick={() => emit({ action: 'open-file', repositoryId: repository.id, path: file.path })}>{labels.openFile}</BaseButton>
        <BaseButton size="sm" disabled={blocked} onClick={() => emit({ action: 'open-diff', repositoryId: repository.id, path: file.path })}>{labels.openDiff}</BaseButton>
      </li>)}</ul>
      <h3>{labels.history}</h3>
      {repository.commits.length === 0 && <p>{labels.empty}</p>}
      <ol className="flex flex-col gap-2">{repository.commits.map(commit => {
        const expanded = model.expandedCommitIds.includes(commit.id);
        const date = commit.date ? new Date(commit.date) : undefined;
        const validDate = date && Number.isFinite(date.getTime());
        return <li key={commit.id}>
          <BaseButton size="sm" disabled={blocked} aria-expanded={expanded} onClick={() => emit({ action: 'toggle-commit', repositoryId: repository.id, commitId: commit.id, expanded: !expanded })}>{commit.shortId} {commit.message}</BaseButton>
          <div className="flex flex-wrap gap-2"><span>{commit.author}</span>{validDate && dateFormatter ? <time dateTime={date.toISOString()}>{dateFormatter.format(date)}</time> : <span>{labels.unknownDate}</span>}{commit.refs?.map(ref => <BaseBadge key={ref} label={ref} />)}</div>
          {expanded && <><ul>{commit.files.slice(0, 8).map(file => <li key={file.path} className="flex gap-2"><span className="break-all">{file.path}</span>{file.status && <BaseBadge label={file.status} />}</li>)}</ul>{commit.files.length > 8 && <p>{labels.remainingFiles}: {commit.files.length - 8}</p>}</>}
        </li>;
      })}</ol>
    </>}
  </section>;
}
